import {Card, CardBody} from "reactstrap";

import ForwardCurveFanChart from "./ForwardCurveFanChart";
import ForwardDistribution from "./ForwardDistribution";
import {ShapeResultContext} from "./DrawShapeQuery";
import {LOOKBACK_LABEL} from "./TickerSeriesPicker";
import {WINDOW_LABELS, WINDOW_LABELS_LONG} from "../utils/windows";

// The dot plugin draws one canvas arc per analog per horizon, and the backend's custom
// grid is `forecastWindow * 2` horizons at daily stride (126 columns for a 3-month
// horizon). Cap the columns so a long horizon stays a readable chart rather than ~12k arcs.
const MAX_HORIZON_COLUMNS = 26;

interface Props {
  context: ShapeResultContext | null;
  forwardCurve: ForwardCurve | null;
  forwardDistribution: ForwardDistribution | null;
}

/** What the resampling actually compares — stated plainly, once, next to the results.
 * A 5-year series resampled onto a 65-point vector is matched as a SHAPE, so the answer
 * is about 3-month windows that traced that shape, not about 5-year histories. */
function comparisonNote(context: ShapeResultContext): string | null {
  if (context.mode !== "ticker" || !context.series) return null;
  const {ticker, lookbackPeriod} = context.series;
  const windowLabel = WINDOW_LABELS[String(context.window)] ?? `${context.window}-day`;
  const period = LOOKBACK_LABEL[lookbackPeriod].phrase;
  return (
    `${ticker}'s last ${period} is resampled onto the ${windowLabel} window and matched on shape: ` +
    `these analogs are ${windowLabel} stretches of history that traced the same path, not ` +
    `${period} histories like it.`
  );
}

export default function ShapeResultsSummary({context, forwardCurve, forwardDistribution}: Props) {
  if (!context) return null;

  // forwardCurve.curve always has one entry per horizon (structural), even when no analog
  // has enough subsequent history to score that far out — so entry counts, not length, are
  // the "is there anything to plot" signal. Mirrors StockChartDisplay's curveWithData.
  const curveWithData = forwardCurve?.curve.some((entry) => entry.count > 0) ? forwardCurve : null;
  const note = comparisonNote(context);
  const committed = forwardCurve?.curve.find((e) => e.horizon === context.forecastWindow);

  if (!curveWithData && !(forwardDistribution && forwardDistribution.count > 0)) return null;

  return (
    <div className="shape-results-summary">
      <Card className="main-chart-card">
        <CardBody>
          {/* The analog count IS the visible cost of a longer horizon: matches_for_shape
              drops every analog whose window doesn't have a full forecast_window of
              subsequent history, so extending the horizon thins this number. Showing it
              against the requested ceiling makes that attrition legible instead of silent. */}
          <div className="shape-results-meta">
            <span>
              <strong>{context.matchCount}</strong> analogs
              {context.matchCount < context.requestedMatches && ` of ${context.requestedMatches} requested`}
            </span>
            <span>
              <strong>{context.forecastWindow}-day</strong> forward horizon
              {committed && committed.count < context.matchCount && ` · ${committed.count} scored at it`}
            </span>
            <span>{WINDOW_LABELS_LONG[context.window] ?? `${context.window} days`} of shape</span>
          </div>
          {note && <p className="shape-results-note">{note}</p>}

          {curveWithData ? (
            <ForwardCurveFanChart curve={curveWithData} showBand maxColumns={MAX_HORIZON_COLUMNS} />
          ) : (
            forwardDistribution && (
              <ForwardDistribution
                distribution={forwardDistribution}
                forecastWindow={context.forecastWindow}
                window={context.window}
              />
            )
          )}
        </CardBody>
      </Card>
    </div>
  );
}
